/**
 * Freezing the trimester's plan, and importing the export taken on the day.
 *
 * ⚠️ IT BELONGS BESIDE THE TARGET, which is why it moved here from the Hiring demand board. The two
 * are one decision taken in one sitting on day one of a trimester: this is the plan, and this is
 * what we commit to delivering against it. Split across two tabs, the freeze was the step people
 * discovered in November — and a baseline taken in November is not a baseline.
 *
 * WHY A BUTTON EXISTS AT ALL. Airtable exposes **no field-level revision history**. A start date
 * that moves leaves no trace, so there is no "read the HDB as it was on 1 September" call to make.
 * The only way a plan survives is if somebody copies it, deliberately, at the start.
 *
 * TWO KINDS. A BASELINE is what the trimester is measured against; a SNAPSHOT is a checkpoint along
 * the way. Freezing a new baseline mid-trimester is allowed — plans get re-cut — and the older one
 * stays as history rather than being overwritten.
 */
import { useState } from 'react';
import { freezeDemandBaseline, importDemandBaseline } from '../../../services/taDemandApi';
import { fetchTracking } from '../../../services/taTrackingApi';
import type { DemandSnapshot } from '../../../types/taDemand';
import { BaselineBar } from '../demand/BaselineBar';
import { Section } from '../tracking/trackingUi';

export function TrimesterBaseline({
  period,
  initialSnapshots = [],
}: {
  period: string;
  initialSnapshots?: DemandSnapshot[];
}) {
  const [snapshots, setSnapshots] = useState<DemandSnapshot[]>(initialSnapshots);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  /**
   * Re-read the list from the tracking payload rather than appending the new snapshot locally: the
   * server decides which one is IN FORCE (a freeze can supersede an existing baseline), and a
   * locally-assembled list would render a bar that disagrees with what the trimester is actually
   * measured against.
   */
  const refresh = async () => {
    const t = await fetchTracking(period).catch(() => null);
    if (t?.snapshots) setSnapshots(t.snapshots);
  };

  const act = async (fn: () => Promise<string>) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      setNotice(await fn());
      await refresh();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="The trimester's baseline" aside="what progress is measured against">
      <BaselineBar
        snapshots={snapshots}
        busy={busy}
        onFreeze={(kind) =>
          void act(async () => {
            const { snapshot } = await freezeDemandBaseline(period, { kind });
            return kind === 'baseline'
              ? `Baseline frozen — ${snapshot.rowCount} roles. This is what the trimester is measured against.`
              : `Snapshot taken — ${snapshot.rowCount} roles.`;
          })
        }
        onImport={(file) =>
          act(async () => {
            const { snapshot, unmapped } = await importDemandBaseline(period, {
              ...file,
              kind: 'baseline',
            });
            return (
              `Frozen ${snapshot.rowCount} roles as the baseline for ${snapshot.takenAt.slice(0, 10)}.` +
              // Naming what did not map is the difference between a baseline and a baseline with
              // silent holes in it.
              (unmapped.length ? ` Columns not recognised: ${unmapped.join(', ')}.` : '')
            );
          })
        }
      />
      {notice ? <p className="mt-2 text-xs text-emerald-700">{notice}</p> : null}
      {error ? <p className="mt-2 text-xs text-rose-700">{error}</p> : null}
    </Section>
  );
}
